import { BusinessPaymentMethod, BusinessPaymentMethodUtil } from "Hooks/Api/apiBusiness";
import { MotorVehicleSearchProductItem } from "Hooks/Api/apiPrice";
import { MvsServicePriceCode, MvsServicePriceCodes } from "Models/Entities/ServicePriceEntity";
import { createContext, useReducer } from "react";
import { validateRego, validateVin } from "Util/StringUtils";
import { ComboboxOption } from "../Combobox/Combobox";
import { AllMvsSearchByOptions, MotorVehicleSearchBy, MotorVehicleSearchRequest, MvsVrnSearchByDefault } from "./MotorVehicleSearchTypes";

export type MvsStage = "form" | "review";

export type GenerateMotorVehicleSearchState = {
	stage: MvsStage,
	data: GenerateMotorVehicleSearchDetail;
}

export type GenerateMotorVehicleSearchDetail = {
	businessPaymentMethod?: BusinessPaymentMethod;
	motorVehicleSearchRequest?: MotorVehicleSearchRequest;
	product?: MotorVehicleSearchProductItem;
	searchByOptions: ComboboxOption<MotorVehicleSearchBy>[]
	isNextValid: boolean,
}

export const initialGenerateMotorVehicleSearchDetail: GenerateMotorVehicleSearchDetail = {
	businessPaymentMethod: undefined,
	motorVehicleSearchRequest: undefined,
	product: undefined,
	searchByOptions: AllMvsSearchByOptions,
	isNextValid: false
}

export const initialGenerateMotorVehicleSearchState: GenerateMotorVehicleSearchState = {
	stage: "form",
	data: initialGenerateMotorVehicleSearchDetail
}

function getProductSpecificSearchOptions(serviceCode: MvsServicePriceCode) {

	// PPSR search has only VRN search
	if (serviceCode === MvsServicePriceCodes.Ppsr) return AllMvsSearchByOptions.filter(x => x.value !== 'RegistrationNumber');

	// All other searches support VIN and VRN
	return AllMvsSearchByOptions;
}

type PayloadAction =
	| { type: "SetStage"; payload: { stage: GenerateMotorVehicleSearchState["stage"] } }
	| { type: "SetServicePrice"; payload: { servicePrice: MotorVehicleSearchProductItem } }
	| { type: "SetBusinessPaymentMethod"; payload: { businessPaymentMethod: BusinessPaymentMethod } }
	| { type: "SetMotorVehicleSearchRequest"; payload: { motorVehicleSearchRequest: MotorVehicleSearchRequest } }
	| { type: "Reset" };

export const MotorVehicleSearchContext = createContext<GenerateMotorVehicleSearchState>(initialGenerateMotorVehicleSearchState);

export const MotorVehicleDispatchContext = createContext<React.Dispatch<PayloadAction>>(() => { });

export function generateMotorVehicleSearchDetailReducer(state: GenerateMotorVehicleSearchState, action: PayloadAction): GenerateMotorVehicleSearchState {
	switch (action.type) {
		case "SetStage":
			return {
				...state,
				stage: action.payload.stage
			}
		case "SetBusinessPaymentMethod":
			return {
				...state,
				data: {
					...state.data,
					businessPaymentMethod: action.payload.businessPaymentMethod,
				}
			};
		case "SetServicePrice":
			const servicePriceProduct = action.payload.servicePrice;
			const searchOptions = getProductSpecificSearchOptions(servicePriceProduct.code);
			const motorVehicleSearchRequest = searchOptions.length === 1 && searchOptions[0].value === 'RegistrationNumber' ? MvsVrnSearchByDefault : undefined;
			return {
				...state,
				data: {
					...state.data,
					// When service price changes, reset selected fields as they are not all compatiable
					motorVehicleSearchRequest: motorVehicleSearchRequest,
					searchByOptions: searchOptions,
					// Update product
					product: servicePriceProduct,
				}
			};
		case "SetMotorVehicleSearchRequest":
			return {
				...state,
				data: {
					...state.data,
					motorVehicleSearchRequest: action.payload.motorVehicleSearchRequest
				}
			};
		case "Reset":
			return initialGenerateMotorVehicleSearchState;
		default:
			return state;
	}
}

function IsValidPayment(context: GenerateMotorVehicleSearchState) {
	const { businessPaymentMethod} = context.data;

	if (!businessPaymentMethod) return false;
	const chargeType = BusinessPaymentMethodUtil.getConsumptionChargeType(businessPaymentMethod);
	if (chargeType === 'PAYG' && !businessPaymentMethod.paymentMethodId) return false;

	return true;
}

function IsValidFormStage(context: GenerateMotorVehicleSearchState) {
	const { product, businessPaymentMethod, motorVehicleSearchRequest } = context.data;

	// Check common required fields
	if (!product) return false;
	if (!businessPaymentMethod?.businessId) return false;

	// Check search specific fields - vin
	if (!motorVehicleSearchRequest?.searchBy) return false;
	if (motorVehicleSearchRequest.searchBy === "Vin") {
		if (!motorVehicleSearchRequest.value) return false;
		if (!validateVin(motorVehicleSearchRequest.value)) return false;
	}

	// Check search specific fields - vrn
	if (motorVehicleSearchRequest.searchBy === "RegistrationNumber") {
		if (motorVehicleSearchRequest.state === "NONE") return false;
		if (!validateRego(motorVehicleSearchRequest.value)) return false;
	}

	// check payment
	if (!IsValidPayment(context)) return false;

	return true;
}

function IsNextValid(context: GenerateMotorVehicleSearchState) {
	if (context.stage === 'form') return IsValidFormStage(context);
	// Curently review just reviews information
	if (context.stage === 'review') return IsValidFormStage(context);
	return false;
}

export const MotorVehicleSearchGenerateWorkflowProvider: React.FunctionComponent = ({ children }) => {
	const [generateMotorVehicleSearchDetail, dispatch] = useReducer(generateMotorVehicleSearchDetailReducer, initialGenerateMotorVehicleSearchState);

	generateMotorVehicleSearchDetail.data.isNextValid = IsNextValid(generateMotorVehicleSearchDetail);

	return (
		<MotorVehicleSearchContext.Provider value={generateMotorVehicleSearchDetail}>
			<MotorVehicleDispatchContext.Provider value={dispatch}>
				{children}
			</MotorVehicleDispatchContext.Provider>
		</MotorVehicleSearchContext.Provider>
	);
}